import { appConfig } from "@shared/utils/appConfig";
import { EventEmitter } from "../services/event-emitter/event-emitter-service";

class AppEventEmitter extends EventEmitter<AppEvent> {}

const allEvts = new Set<string>();
function regEvt<T extends string>(evt: T) {
  if (allEvts.has(evt)) {
    throw new Error(`Duplicated event ${evt}`);
  }
  allEvts.add(evt);
  return evt;
}

// AUTH
const AuthLogoutEvt = regEvt("sidekick-auth-logout");
const AuthLoginEvt = regEvt("sidekick-auth-login");
const AuthLoginChallengeEvt = regEvt("sidekick-auth-login-challenge");
const AuthIdentifyUserEvt = regEvt("sidekick-auth-identify-user");
interface AuthLogout { eventType: typeof AuthLogoutEvt; }
interface AuthLogin { eventType: typeof AuthLoginEvt; }
interface AuthLoginChallenge { eventType: typeof AuthLoginChallengeEvt; challengeName: string; }
interface AuthIdentifyUser {
  eventType: typeof AuthIdentifyUserEvt;
  trackingId: string;
  firstName: string;
  lastName: string;
  mlsProviders: string;
  subscriptionPlanId: string;
  roles: string;
}

// PROMPT
const PromptRequestSuccessEvt = regEvt("sidekick-prompt-request-success");
const PromptRequestFailureEvt = regEvt("sidekick-prompt-request-failure");
const PromptResponseSuccessEvt = regEvt("sidekick-prompt-response-success");
const PromptResponseFailureEvt = regEvt("sidekick-prompt-response-failure");
interface PromptRequestSuccess { eventType: typeof PromptRequestSuccessEvt; }
interface PromptRequestFailure { eventType: typeof PromptRequestFailureEvt; }
interface PromptResponseSuccess { eventType: typeof PromptResponseSuccessEvt; responseTimeMs: number }
interface PromptResponseFailure { eventType: typeof PromptResponseFailureEvt; }

// REGISTRATION
const RegistrationStartSuccessEvt = regEvt("sidekick-registration-start-success");
const RegistrationStartFailureEvt = regEvt("sidekick-registration-start-failure");
const RegistrationPersonalInformationStepEvt = regEvt("sidekick-registration-personal-information-step");
const RegistrationPaymentStepEvt = regEvt("sidekick-registration-payment-step");
const RegistrationChangeEmailStartEvt = regEvt("sidekick-registration-change-email-start");
const RegistrationChangeEmailSuccessEvt = regEvt("sidekick-registration-change-email-success");
const RegistrationCompleteSuccessEvt = regEvt("sidekick-registration-complete-success");
const RegistrationCompleteFailureEvt = regEvt("sidekick-registration-complete-failure");
interface RegistrationStartSuccess { eventType: typeof RegistrationStartSuccessEvt; }
interface RegistrationStartFailure { eventType: typeof RegistrationStartFailureEvt; }
interface RegistrationPersonalInformationStep { eventType: typeof RegistrationPersonalInformationStepEvt; }
interface RegistrationPaymentStep { eventType: typeof RegistrationPaymentStepEvt; }
interface RegistrationChangeEmailSuccess { eventType: typeof RegistrationChangeEmailSuccessEvt; }
interface RegistrationChangeEmailStart { eventType: typeof RegistrationChangeEmailStartEvt; }
interface RegistrationCompleteSuccess { eventType: typeof RegistrationCompleteSuccessEvt; }
interface RegistrationCompleteFailure { eventType: typeof RegistrationCompleteFailureEvt; }

//WELCOME FLOW
const WelcomeFlowStartEvt = regEvt("sidekick-welcome-flow-start");
const WelcomeFlowCompleteEvt = regEvt("sidekick-welcome-flow-complete");
interface WelcomeFlowStart { eventType: typeof WelcomeFlowStartEvt; }
interface WelcomeFlowComplete { eventType: typeof WelcomeFlowCompleteEvt; }

// SUGGESTIONS
const SuggestionsShowClickEvt = regEvt("sidekick-suggestions-show-click");
interface SuggestionsShowClick { eventType: typeof SuggestionsShowClickEvt; }
const SuggestionsHideClickEvt = regEvt("sidekick-suggestions-hide-click");
interface SuggestionsHideClick { eventType: typeof SuggestionsHideClickEvt; }
const SuggestionsSelectClickEvt = regEvt("sidekick-suggestions-select-click");
interface SuggestionsSelectClick { eventType: typeof SuggestionsSelectClickEvt; }
const SuggestionsRefreshClickEvt = regEvt("sidekick-suggestions-refresh-click");
interface SuggestionsRefreshClick { eventType: typeof SuggestionsRefreshClickEvt; }
const SuggestionsRequestSuccessEvt = regEvt("sidekick-suggestions-request-success");
interface SuggestionsRequestSuccess { eventType: typeof SuggestionsRequestSuccessEvt; }
const SuggestionsRequestFailureEvt = regEvt("sidekick-suggestions-request-failure");
interface SuggestionsRequestFailure { eventType: typeof SuggestionsRequestFailureEvt; }

// LEARNING CENTER
const LearningVideoClickEvt = regEvt("sidekick-learning-video-click");
interface LearningVideoClick { eventType: typeof LearningVideoClickEvt; videoId: string; videoTitle: string; }

if (appConfig.NODE_ENV === "development" && !appConfig.HIDE_ALL_APP_EVENTS) {
  console.debug("ALL_EVENTS", allEvts);
}

export type AppEvent =
  | AuthLogout
  | AuthLogin
  | AuthLoginChallenge
  | AuthIdentifyUser
  | PromptRequestSuccess
  | PromptRequestFailure
  | PromptResponseSuccess
  | PromptResponseFailure
  | RegistrationStartSuccess
  | RegistrationStartFailure
  | RegistrationPersonalInformationStep
  | RegistrationPaymentStep
  | RegistrationChangeEmailSuccess
  | RegistrationChangeEmailStart
  | RegistrationCompleteSuccess
  | RegistrationCompleteFailure
  | WelcomeFlowStart
  | WelcomeFlowComplete
  | SuggestionsShowClick
  | SuggestionsHideClick
  | SuggestionsSelectClick
  | SuggestionsRefreshClick
  | SuggestionsRequestSuccess
  | SuggestionsRequestFailure
  | LearningVideoClick

const singletonEmitter = new AppEventEmitter();

(window as any).appEventEmitter = singletonEmitter;

export const appEventEmitter = singletonEmitter;
